'use client'

import React from 'react'

import { cn } from '../../lib/utils'

interface LoadingSpinnerProps {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  color?: 'primary' | 'secondary' | 'white' | 'black' | 'gray'
  className?: string
  text?: string
  textPosition?: 'left' | 'right' | 'top' | 'bottom'
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  color = 'primary',
  className,
  text,
  textPosition = 'right',
}) => {
  // Size mapping
  const sizeMap = {
    xs: 'w-3 h-3',
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
    xl: 'w-12 h-12',
  }

  // Color mapping
  const colorMap = {
    primary: 'text-indigo-600',
    secondary: 'text-blue-500',
    white: 'text-white',
    black: 'text-black',
    gray: 'text-gray-500',
  }

  // Text positioning
  const containerDirection = {
    left: 'flex-row-reverse items-center',
    right: 'flex-row items-center',
    top: 'flex-col-reverse items-center',
    bottom: 'flex-col items-center',
  }

  const textMargin = {
    left: 'mr-3',
    right: 'ml-3',
    top: 'mb-2',
    bottom: 'mt-2',
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn('flex', containerDirection[textPosition], className)}
    >
      <svg
        className={cn('animate-spin', sizeMap[size], colorMap[color])}
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        ></circle>
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        ></path>
      </svg>

      {text && (
        <span className={cn('text-sm font-medium', colorMap[color], textMargin[textPosition])}>
          {text}
        </span>
      )}

      <span className="sr-only">Loading</span>
    </div>
  )
}

export default LoadingSpinner
